<template>
  <div id="map" class="map" />
</template>

<script>
import mapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import booleanContains from '@turf/boolean-contains'
import { featureCollection } from '@turf/helpers'
import mapbox from 'mapbox-gl'

import config from '@/components/config'
import { mapboxLayerTypes, mapboxSourceTypes } from '@/components/mixins/constants'

import { filterUnique2dArray } from '@/utils'
import {
  coordinatesFromFeatureCollection,
  featureCollectionHasCoordinates,
  polygonFeatureFromCoordinates,
  uniqueCoordinatesFromFeatureCollection,
} from '@/utils/geojson'
import mapUtils from '@/utils/mapbox'

export default {
  name: 'Map',
  data() {
    return {
      map: null,
      draw: null,
      formData: {
        drawnBounds: null,
      },
      controls: [],
      drawCoordinates: null,
    }
  },
  props: {
    allowFourNewCoordinatesOnly: { type: Boolean, default: false },
    center: { type: Object, required: false },
    customControls: { type: Array, default: () => [] },
    editablePolygonFeature: { type: Object, default: null },
    enableDraw: { type: Boolean, default: false },
    enableFullscreen: { type: Boolean, default: true },
    enableGeocoder: { type: Boolean, default: false },
    enableNavigationControls: { type: Boolean, default: true },
    fitBounds: { type: Array, default: () => [] },
    parentPolygon: { type: Object, default: null },
    childPolygons: { type: Array, default: () => [] },
    staticPolygons: { type: Array, default: () => [] },
    zoomedOut: { type: Boolean, default: false },
  },
  mounted() {
    this.initMap()
  },
  computed: {
    editablePolygonFeatureHasImage() {
      return !!this.editablePolygonFeature?.properties?.imageUrl
    },
  },
  methods: {
    initMap() {
      if (this.enableGeocoder) {
        this.controls.push(
          new mapboxGeocoder({
            accessToken: config.MAPBOX_TOKEN,
            mapboxgl: mapbox,
            marker: false,
          }),
        )
      }
      if (this.editablePolygonFeatureHasImage) {
        this.controls.push(
          mapUtils.initCustomControl({
            clickFunction: this.rotateFeaturePolygon,
            materialIcon: 'sync',
          }),
        )
      }

      this.map = mapUtils.initMap({
        container: 'map',
        enableFullscreen: this.enableFullscreen,
        enableNavigationControls: this.enableNavigationControls,
        controls: [...this.controls, ...this.customControls],
        zoom: this.zoomedOut ? config.MAP_DEFAULT_ZOOMED_OUT : config.MAP_DEFAULT_ZOOM,
      })

      if (this.enableDraw || this.editablePolygonFeature) {
        this.initDrawTools()
      }

      this.map.on('load', () => {
        if (this.editablePolygonFeature) {
          this.drawEditablePolygonOnMap()
        }
        if (this.staticPolygons?.length > 0) {
          this.addStaticPolygonsToMap()
        }
        if (this.fitBounds?.length > 0) {
          this.fitMapBounds()
        }
      })
    },
    fitMapBounds() {
      this.$nextTick(() => {
        this.map.fitBounds(this.fitBounds, {
          padding: config.MAPBOX_FIT_BOUNDS_PADDING,
          animate: false,
        })
      })
    },
    rotateFeaturePolygon() {
      const coordinates = uniqueCoordinatesFromFeatureCollection(this.draw.getAll())
      const newCoordinates = [coordinates[3], ...coordinates.slice(0, 3)]
      this.updateImage(this.editablePolygonFeature, newCoordinates)
      // see if i can boil down the updates by using either of these
      this.draw.set(featureCollection([polygonFeatureFromCoordinates(newCoordinates, {})]))
      this.drawCoordinates = uniqueCoordinatesFromFeatureCollection(this.draw.getAll())
      this.emitCoordinatesUpdate()
    },
    updateImage(feature, newCoordinates) {
      const source = this.map.getSource(`imageFeature:${feature.properties.id}`)
      source.updateImage({
        url: feature.properties.imageUrl,
        coordinates: newCoordinates.slice(0, 4),
      })
    },
    emitCoordinatesUpdate({ sorted = false } = {}) {
      const coordinates = mapUtils.coordinatePairArrayToLngLatObject(this.drawCoordinates)
      this.$emit('coordinates-updated', sorted ? mapUtils.sortCoordinatesClockwise(coordinates) : this.drawCoordinates)
    },
    initDrawTools() {
      this.draw = mapUtils.initPolygonDraw({
        polygon: this.enableDraw ?? false,
        trash: this.enableDraw ?? false,
      })
      this.map.addControl(this.draw)
      this.addDrawEventListeners()
    },
    addDrawEventListeners() {
      this.map.on('draw.modechange', event => {
        this.initDrawModeChangeEventListeners(event)
      })
      this.map.on('draw.create', () => {
        this.initDrawCreateEventListeners()
      })
      this.map.on('draw.update', newFeatureCollection => {
        this.initDrawUpdateEventListeners(newFeatureCollection)
      })
      this.map.on('draw.delete', () => {
        this.emitCoordinatesReset()
      })
      this.map.on('click', () => {
        this.initMapClickEventListeners()
      })
    },
    emitCoordinatesReset() {
      this.$emit('coordinates-reset')
    },
    initDrawModeChangeEventListeners(event) {
      const { mode } = event
      // Delete old drawing when present because we only want to allow 1 polygon at a time
      if (mode === this.draw.modes.DRAW_POLYGON && this.drawCoordinates?.length > 0) {
        this.draw.deleteAll()
        this.emitCoordinatesReset()
        // Draw is getting stuck after deleting - changing the mode back and forth solves it
        this.draw.changeMode(this.draw.modes.SIMPLE_SELECT)
        this.draw.changeMode(this.draw.modes.DRAW_POLYGON)
      }
    },
    initDrawCreateEventListeners() {
      this.drawCoordinates = uniqueCoordinatesFromFeatureCollection(this.draw.getAll())
      if (this.parentPolygon) {
        const createdPolygonFeature = polygonFeatureFromCoordinates(this.drawCoordinates)
        // Reset polygon if it is outside of parent polygon
        if (!booleanContains(this.parentPolygon, createdPolygonFeature)) {
          this.$notify({
            group: 'alert',
            type: 'error',
            title: this.$i18n.t('map.pointOutsideAllowedArea'),
          })
          this.draw.deleteAll()
          return
        }
      }
      this.emitCoordinatesUpdate({
        sorted: this.editablePolygonFeatureHasImage || this.allowFourNewCoordinatesOnly,
      })
    },
    initDrawUpdateEventListeners(newFeatureCollection) {
      const previousDrawCoordinates = this.drawCoordinates
      this.drawCoordinates = uniqueCoordinatesFromFeatureCollection(this.draw.getAll())
      const updatedPolygonFeature = polygonFeatureFromCoordinates(this.drawCoordinates)
      if (this.parentPolygon) {
        // Reset coordinates when update would move the child out of the parent polygon
        if (!booleanContains(this.parentPolygon, updatedPolygonFeature)) {
          this.resetToPreviousDrawCoordinates(previousDrawCoordinates)
        }
      }
      if (this.childPolygons.length) {
        // Reset coordinates if any child is left outside of the polygon
        const childrenContained = this.childPolygons.every(child => booleanContains(updatedPolygonFeature, child))
        if (!childrenContained) {
          this.resetToPreviousDrawCoordinates(previousDrawCoordinates)
        }
      }
      if (this.editablePolygonFeatureHasImage) {
        const newCoordinates = coordinatesFromFeatureCollection(newFeatureCollection)
        if (newCoordinates.length <= 5) {
          this.updateImage(this.editablePolygonFeature, newCoordinates)
        } else {
          this.drawCoordinates = this.editablePolygonFeature.geometry.coordinates[0]
          this.draw.set(featureCollection([this.editablePolygonFeature]))
          this.$notify({
            group: 'alert',
            type: 'error',
            title: this.$i18n.t('map.addingPointsDisallowed'),
          })
          this.updateImage(this.editablePolygonFeature, this.editablePolygonFeature.geometry.coordinates[0])
        }
      } else if (this.allowFourNewCoordinatesOnly) {
        const newCoordinates = coordinatesFromFeatureCollection(newFeatureCollection)
        if (newCoordinates.length > 4) {
          this.drawCoordinates = null
          this.draw.set(featureCollection([]))
          this.$notify({
            group: 'alert',
            type: 'error',
            title: this.$i18n.t('map.addingPointsDisallowed'),
          })
        }
      }

      if (this.drawCoordinates?.length > 0) {
        this.emitCoordinatesUpdate({
          sorted: this.editablePolygonFeatureHasImage || this.allowFourNewCoordinatesOnly,
        })
      }
    },
    resetToPreviousDrawCoordinates(previousCoordinates) {
      this.$notify({
        group: 'alert',
        type: 'error',
        title: this.$i18n.t('map.pointOutsideAllowedArea'),
      })
      this.drawCoordinates = previousCoordinates ?? this.editablePolygonFeature.geometry.coordinates[0]
      this.draw.set(featureCollection([polygonFeatureFromCoordinates(this.drawCoordinates)]))
    },
    initMapClickEventListeners() {
      // There is no dedicated event for each draw click - only for the
      // creation of the whole polygon, which we want to prevent for
      // coordinates more than 5 because it will mess up site images
      if (this.draw.getMode() !== this.draw.modes.DRAW_POLYGON) {
        return
      }
      const drawFeatureCollection = this.draw.getAll()
      // Only assign coordinates when they are present
      if (featureCollectionHasCoordinates(drawFeatureCollection)) {
        this.drawCoordinates = coordinatesFromFeatureCollection(drawFeatureCollection)
      }
      if (this.editablePolygonFeatureHasImage || this.allowFourNewCoordinatesOnly) {
        // Reject more than 4 unique coordinates
        if (filterUnique2dArray(this.drawCoordinates).length > 4) {
          this.$notify({
            group: 'alert',
            type: 'error',
            text: this.$t('map.tooManyCoordinates'),
          })
          this.draw.deleteAll()
          this.draw.changeMode(this.draw.modes.SIMPLE_SELECT)
        }
      }
    },
    drawEditablePolygonOnMap() {
      this.draw.set(featureCollection([this.editablePolygonFeature]))
      if (this.editablePolygonFeatureHasImage) {
        this.addImagePolygon(this.editablePolygonFeature)
      }
    },
    addStaticPolygonsToMap() {
      for (const staticPolygon of this.staticPolygons) {
        const { imageUrl } = staticPolygon.properties
        if (imageUrl) {
          this.addImagePolygon(staticPolygon, true)
        } else {
          this.addStaticPolygon(staticPolygon)
        }
      }
    },
    addImagePolygon(feature, addOutline) {
      const sourceName = `imageFeature:${feature.properties.id}`
      this.map.addSource(sourceName, {
        type: mapboxSourceTypes.IMAGE,
        url: feature.properties.imageUrl,
        coordinates: feature.properties.imageCoordinates,
      })
      this.map.addLayer({
        id: `imageLayer:${feature.properties.id}`,
        type: mapboxLayerTypes.RASTER,
        paint: { 'raster-opacity': 0.4, 'raster-fade-duration': 0 },
        source: sourceName,
      })
      this.map.addLayer({
        id: `outline:${sourceName}`,
        type: mapboxLayerTypes.LINE,
        source: sourceName,
        layout: {},
        ...(addOutline && {
          paint: {
            'line-color': '#314053',
            'line-width': 25,
          },
        }),
      })
    },
    addStaticPolygon(polygonFeature) {
      const name = `staticPolygonSource:${polygonFeature.properties.id}`
      this.addStaticSource(name, polygonFeature)
      this.addStaticOutline(name)
      this.fillStaticPolygonBackground(name, polygonFeature)
    },
    addStaticSource(name, feature) {
      this.map.addSource(name, {
        type: mapboxSourceTypes.GEOJSON,
        data: feature,
      })
    },
    addStaticOutline(name) {
      this.map.addLayer({
        id: `staticOutlineLayer:${name}`,
        type: mapboxLayerTypes.LINE,
        source: name,
      })
    },
    fillStaticPolygonBackground(name, feature) {
      const uppercasedTypeName = feature?.properties?.type?.toUpperCase()
      this.map.addLayer({
        id: `staticPolygonFillLayer:${name}`,
        type: mapboxLayerTypes.FILL,
        source: name,
        ...(!!uppercasedTypeName && {
          paint: {
            'fill-color': config.MAPBOX_BOX_COLORS[uppercasedTypeName],
            'fill-opacity': config.MAPBOX_BOX_OPACITY[uppercasedTypeName],
          },
        }),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.map {
  width: 100%;
  height: 400px;
}
</style>
